import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Add from '@mui/icons-material/Add';
import AutoFixHigh from '@mui/icons-material/AutoFixHigh';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast, Flip } from 'react-toastify';

import { setSelectedLanguage } from '@modules/subtitle';
import api from '@utils/axiosInstance';
import langListData from '@resources/subLangList.json';

function NoSubContent({
    availableSubtitleLanguages,
    setAvailableSubtitleLanguages,
    setCurrentSelectedLanguage,
    setIsAddModalOpen,
    setShowNoSub
}) {
    const dispatch = useDispatch();
    const { uuid } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const { userToken, organizationData } = useSelector(state => state.organization);

    const [selectedSubtitleLanguage, setSelectedSubtitleLanguage] = useState(null);
    const [availableLanguageOptions, setAvailableLanguageOptions] = useState([]);
    const [isSubtitleCreationInProgress, setIsSubtitleCreationInProgress] = useState(false);

    // 자막 생성되지 않은 언어 필터링
    useEffect(() => {
        const filtered = langListData.languages.filter(
            lang => !availableSubtitleLanguages.some(sub => sub.lang === lang.lang)
        );
        setAvailableLanguageOptions(filtered);
    }, [availableSubtitleLanguages]);

    // 자막 생성 완료 확인 함수
    const checkSubtitleGenerationComplete = async (lang: string, maxAttempts = 10) => {
        let attempts = 0;

        const checkComplete = async (): Promise<boolean> => {
            try {
                const response = await api({
                    method: 'post',
                    url: '/subtitle/getSubtitle',
                    data: {
                        tk: userToken,
                        uuid,
                        ogc: organizationData.organization_code,
                        lang: lang
                    }
                });

                // 자막 내용이 실제로 존재하는지 확인
                if (response.data.code === 100 && response.data.data) {
                    const subtitleData = response.data.data;
                    // 자막 내용이 있고 길이가 0보다 큰지 확인
                    if (subtitleData && subtitleData.length > 0) {
                        return true;
                    }
                }
                return false;
            } catch (error) {
                console.error('Error checking subtitle generation:', error);
                return false;
            }
        };

        while (attempts < maxAttempts) {
            const isComplete = await checkComplete();
            if (isComplete) {
                return true;
            }

            // 1초 대기 후 다시 확인
            await new Promise(resolve => setTimeout(resolve, 1000));
            attempts++;
        }

        return false;
    };

    // 자막 생성 or 자동 생성 처리
    const createOrGenerateSubtitle = async (isAuto = false) => {
        if (!selectedSubtitleLanguage) {
            toast.error(t('vod.manage.subtitle_tab.select_language'));
            return;
        }

        setIsSubtitleCreationInProgress(true);
        const toastId = toast.loading(
            isAuto
                ? t('vod.manage.subtitle_tab.auto_generating_subtitle')
                : t('vod.manage.subtitle_tab.loading'),
            {
                position: "top-center",
                className: 'rotateY animated',
                transition: Flip,
                autoClose: false, // 자동 생성 중에는 자동으로 닫히지 않도록
            }
        );

        try {
            const endpoint = isAuto ? '/subtitle/generate' : '/subtitle/create';
            api({
                method: 'post',
                url: endpoint,
                data: {
                    tk: userToken,
                    uuid,
                    ogc: organizationData.organization_code,
                    lang: selectedSubtitleLanguage.lang
                }
            })
                .then(async response => {
                    if (response.data.code !== 100) {
                        throw new Error(
                            isAuto
                                ? t('vod.manage.subtitle_tab.auto_creation_failed')
                                : t('vod.manage.subtitle_tab.subtitle_creation_failed')
                        );
                    } else {
                        const newSubtitle = {
                            uuid,
                            lang: selectedSubtitleLanguage.lang,
                            state: 0
                        };

                        const subtitleData = isAuto ? response.data.data : newSubtitle;

                        // 자막 목록에 새 자막 추가
                        setAvailableSubtitleLanguages(prev => [...prev, subtitleData]);

                        // 현재 선택된 언어를 새로 생성된 자막으로 설정
                        setCurrentSelectedLanguage(subtitleData);
                        dispatch(setSelectedLanguage(subtitleData));

                        // UI 상태 업데이트
                        setIsAddModalOpen(false);
                        setShowNoSub(false);
                        setAvailableLanguageOptions([]);

                        // 성공 메시지 표시
                        if (!isAuto) {
                            toast.update(toastId, {
                                render: t('vod.manage.subtitle_tab.empty_subtitle'),
                                type: "success",
                                isLoading: false,
                                autoClose: 3000
                            });
                        } else {
                            toast.update(toastId, {
                                render: t('vod.manage.subtitle_tab.auto_creation_success'),
                                type: "success",
                                isLoading: false,
                                autoClose: 3000
                            });
                        }

                        // 자동 생성인 경우 생성 완료를 확인한 후 이동
                        if (isAuto) {
                            console.log('Checking subtitle generation completion...');
                            const isComplete = await checkSubtitleGenerationComplete(selectedSubtitleLanguage.lang);

                            if (isComplete) {
                                console.log('Subtitle generation completed, navigating to language');
                                const pathSegments = location.pathname.split('/');
                                const subtitleIndex = pathSegments.findIndex((segment: string) => segment === 'subtitle');

                                if (subtitleIndex !== -1) {
                                    const newPath = pathSegments
                                        .slice(0, subtitleIndex + 1)
                                        .join('/') + '/' + selectedSubtitleLanguage.lang;

                                    navigate(newPath, { replace: true });
                                }
                            } else {
                                console.log('Subtitle generation timeout, navigating anyway');
                                // 타임아웃이어도 이동
                                const pathSegments = location.pathname.split('/');
                                const subtitleIndex = pathSegments.findIndex((segment: string) => segment === 'subtitle');

                                if (subtitleIndex !== -1) {
                                    const newPath = pathSegments
                                        .slice(0, subtitleIndex + 1)
                                        .join('/') + '/' + selectedSubtitleLanguage.lang;

                                    navigate(newPath, { replace: true });
                                }
                            }
                        } else {
                            // 일반 생성인 경우 즉시 이동
                            const pathSegments = location.pathname.split('/');
                            const subtitleIndex = pathSegments.findIndex((segment: string) => segment === 'subtitle');

                            if (subtitleIndex !== -1) {
                                const newPath = pathSegments
                                    .slice(0, subtitleIndex + 1)
                                    .join('/') + '/' + selectedSubtitleLanguage.lang;

                                console.log('Navigating to new path:', newPath);
                                navigate(newPath, { replace: true });
                            }
                        }
                    }
                })
                .catch(err => {
                    console.error('Subtitle creation error:', err);
                    toastId ? toast.update(toastId, {
                        render: t('common.error'),
                        type: "error",
                        isLoading: false,
                        autoClose: 3000
                    })
                        : toast.error(t('common.error'));
                    setIsSubtitleCreationInProgress(false);
                    setShowNoSub(false);
                })
                .finally(() => {
                    setIsSubtitleCreationInProgress(false);
                });
        } catch (error) {
            toast.update(toastId, {
                render: error.message,
                type: "error",
                isLoading: false,
                autoClose: 3000
            });
        }
    };

    return (
        <Box className="no-subcontent-root">
            <Box className="no-subcontent-inner">
                <Typography variant="h6" className="no-subcontent-title">
                    {t('vod.manage.subtitle_tab.create_subtitle')}
                </Typography>

                <Typography variant="body1" className="no-subcontent-desc">
                    {t('vod.manage.subtitle_tab.select_language')}
                </Typography>

                <FormControl variant="outlined" className="no-subcontent-form">
                    <Autocomplete
                        options={availableLanguageOptions}
                        getOptionLabel={(opt) => t(`languages.${opt.lang}`)}
                        onChange={(e, newValue) => setSelectedSubtitleLanguage(newValue)}
                        noOptionsText={t('vod.manage.subtitle_tab.no_matching_data')}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('vod.manage.subtitle_tab.selected_language')}
                                className="no-subcontent-textfield"
                            />
                        )}
                    />
                </FormControl>

                <Typography variant="body1" className="no-subcontent-selected-lang">
                    {selectedSubtitleLanguage
                        ? `${t('vod.manage.subtitle_tab.selected_language')}: ${t(`languages.${selectedSubtitleLanguage.lang}`)}`
                        : ''}
                </Typography>

                <Box className="no-subcontent-btn-group">
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        color="info"
                        onClick={() => createOrGenerateSubtitle(false)}
                        disabled={isSubtitleCreationInProgress || !selectedSubtitleLanguage}
                        fullWidth
                    >
                        {isSubtitleCreationInProgress
                            ? t('vod.manage.subtitle_tab.loading')
                            : t('vod.manage.subtitle_tab.empty_subtitle')}
                    </Button>

                    <Button
                        variant="contained"
                        startIcon={<AutoFixHigh />}
                        color="primary"
                        onClick={() => createOrGenerateSubtitle(true)}
                        disabled={
                            isSubtitleCreationInProgress ||
                            !selectedSubtitleLanguage ||
                            !['ko', 'en', 'ja'].includes(selectedSubtitleLanguage.lang)
                        }
                        fullWidth
                    >
                        {isSubtitleCreationInProgress
                            ? t('vod.manage.subtitle_tab.loading')
                            : t('vod.manage.subtitle_tab.auto_create_subtitle')}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default NoSubContent;